import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { FormSummaryArrowSvg } from "@/src/svg/applyOnline/FormSummaryArrowSvg";
import { ApplyFormSummaryConst } from "@/src/constants/apply-online";
import { FileFieldName, IApplyOnline, IFormApplicationProps } from "@/src/types/applyOnline";
import FormSummaryModal, { FileAttachmentField } from "../FormSummaryModal/FormSummaryModal";
import { ApplyOnlineDocSvg } from "@/src/svg/applyOnline/ApplyOnlineDocSvg";
import { RippleWrapper } from "@/src/components/Ripple/RippleWrapper";

const applyOnlineFileFields: FileAttachmentField[] = [
  { name: "filePassport", title: "International Passport" },
  { name: "fileCertificate", title: "School Certificate" },
  { name: "fileEducation", title: "Previous Education" },
  { name: "fileAnouther", title: "Additional Files" },
];

const FormSummary: FC<IFormApplicationProps> = ({
  formData,
  handleSubmit,
  register,
  setValue,
  selectedFilesMap,
  setSelectedFilesMap,
  isSubmitDisabled,
}) => {
  const prevFormData = useRef<IApplyOnline | undefined>(null);
  const [activeBlock, setActiveBlock] = useState<string | null>("fast");
  const [fileModalIsActive, setFileModalIsActive] = useState<boolean>(false);
  useEffect(() => {
    if (!formData || !prevFormData.current) {
      prevFormData.current = formData;
      return;
    }

    let lastChangedField: string | null = null;

    const isFilesArray = (val: any): val is File[] => Array.isArray(val);

    for (const key in formData) {
      const newVal = formData[key as keyof IApplyOnline];
      const oldVal = prevFormData.current[key as keyof IApplyOnline];

      if (isFilesArray(newVal) && isFilesArray(oldVal)) {
        const isSame =
          newVal.length === oldVal.length && newVal.every((file, i) => file.name === oldVal[i].name && file.size === oldVal[i].size);

        if (!isSame) lastChangedField = key;
      } else {
        if (newVal !== oldVal) lastChangedField = key;
      }
    }

    if (lastChangedField) {
      const fast = ["fullname", "office", "phone", "email", "filePassport", "fileCertificate", "fileEducation"];
      const dop = ["birthDate", "nationality", "address", "country"];
      const program = ["levelEducation", "speciality", "formStudy", "comment"];
      if (fast.includes(lastChangedField)) setActiveBlock("fast");
      else if (dop.includes(lastChangedField)) setActiveBlock("dop-info");
      else if (program.includes(lastChangedField)) setActiveBlock("program-details");
    }

    prevFormData.current = formData;
  }, [formData]);

  const toggleBlock = (id: string) => {
    setActiveBlock((prev) => (prev === id ? null : id));
  };

  const handleClickDocument = (action: "open" | "close") => {
    setFileModalIsActive(action === "open");
    const applyOnlineSteps = document.querySelector(".apply-online-steps");
    const body = document.querySelector("body");
    if (action === "open") {
      (applyOnlineSteps as HTMLElement).style.zIndex = "-1";
      (body as HTMLElement).style.overflow = "hidden";
    } else {
      (applyOnlineSteps as HTMLElement).style.zIndex = "10";
      (body as HTMLElement).style.overflow = "";
    }
  };

  return (
    <div className="form-summary">
      <div className="form-summary-content">
        <h1 className="form-summary-title">Summary</h1>
        {ApplyFormSummaryConst.map((section) => (
          <div className={`form-summary-prev ${section.id}`} key={section.id}>
            <div className="form-summary-prev-header" onClick={() => toggleBlock(section.id)}>
              <h1 className="form-summary-prev-title">{section.title}</h1>
              <div className={`chevron-icon ${activeBlock === section.id ? "active" : ""}`}>
                <FormSummaryArrowSvg />
              </div>
            </div>

            {activeBlock === section.id && (
              <div className="form-summary-prev-body">
                {section.fields.map((field) => {
                  if (field.className === "comment") return null;
                  return (
                    <div className="form-summary-prev-field" key={field.className}>
                      <p className={`form-summary-prev-field-name ${field.className}`}>{field.name}:</p>
                      <p className={`form-summary-prev-field-value ${field.className}`}>
                        {String(formData?.[field.className as keyof IApplyOnline] ?? "-")}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="form-summary-buttons">
        <RippleWrapper>
          <div
            className="form-summary-buttons-file-button"
            onClick={() => {
              setTimeout(() => handleClickDocument("open"), 200);
            }}
          >
            <span>Attach Files </span>
            <ApplyOnlineDocSvg />
          </div>
        </RippleWrapper>

        <div className="form-summary-buttons-file-quantity">
          <span>Attached: </span>
          <span>{selectedFilesMap ? Object.values(selectedFilesMap).flat().length : "0"} file(s)</span>
        </div>
        <button
          className={`form-summary-buttons-submit ${isSubmitDisabled ? "disabled" : ""}`}
          disabled={isSubmitDisabled}
          type="button"
          onClick={handleSubmit}
        >
          Submit
        </button>
      </div>
      <FormSummaryModal
        isActive={fileModalIsActive}
        onClose={() => handleClickDocument("close")}
        fields={applyOnlineFileFields}
        selectedFilesMap={selectedFilesMap as Record<FileFieldName, File[]>}
        onAttach={(filesMap) => {
          setSelectedFilesMap?.(filesMap as Record<FileFieldName, File[]>);
          Object.entries(filesMap).forEach(([key, files]) => {
            setValue?.(key as keyof IApplyOnline, files, {
              shouldValidate: true,
              shouldDirty: true,
            });
          });
        }}
      />
    </div>
  );
};

export default FormSummary;
